/**
 * @copyright
 * (c) 2026 netTrek GmbH & Co. KG
 *
 * Normalizes a decoded-CBOR value into the JSON-friendly shape c2pa-rs emits
 * (and that the C2paManifestHelper expects):
 * - byte strings (`Uint8Array`) → `number[]` (matches c2pa-rs `HashedUri.hash` etc.)
 * - `Map` (non-text-keyed CBOR maps) → plain object with stringified keys
 * - `CborTagged` → its inner value (tags carry no extra meaning for assertion data)
 * - arrays/objects → recursively normalized
 * - object properties whose value is `null`/`undefined` are dropped, matching
 *   c2pa-rs's serde `skip_serializing_if = Option::is_none` JSON output (CBOR
 *   carries explicit nulls for absent optionals; the JSON omits them). Nulls
 *   inside arrays are preserved.
 */

import { CborTagged } from '../cbor/decode'

export function toJsonValue (value: unknown): unknown {
    if (value instanceof Uint8Array) {
        return Array.from(value)
    }
    if (value instanceof CborTagged) {
        return toJsonValue(value.value)
    }
    if (Array.isArray(value)) {
        return value.map(toJsonValue)
    }
    if (value instanceof Map) {
        const obj: Record<string, unknown> = {}
        for (const [k, v] of value.entries()) {
            const nv = toJsonValue(v)
            if (nv !== null && nv !== undefined) obj[String(k)] = nv
        }
        return obj
    }
    if (value && typeof value === 'object') {
        const obj: Record<string, unknown> = {}
        for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
            const nv = toJsonValue(v)
            if (nv !== null && nv !== undefined) obj[k] = nv
        }
        return obj
    }
    return value
}
